import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { SITE_URL, FAVICON_URL, parseRoute } from './useSeo';

const JSONLD_IDS = [
  'jsonld-website',
  'jsonld-organization',
  'jsonld-article',
  'jsonld-breadcrumb',
];

function upsertJsonLd(id: string, data: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id: string): void {
  const el = document.getElementById(id);
  if (el) {
    el.remove();
  }
}

function clearJsonLd(): void {
  JSONLD_IDS.forEach(removeJsonLd);
}

export function useJsonLd(): void {
  const location = useLocation();

  useEffect(() => {
    const route = parseRoute(location.pathname);
    const { lang, slug } = route;

    clearJsonLd();

    if (slug) {
      const article = articles.find((a) => a.slug === slug);
      if (article) {
        const url = `${SITE_URL}/${lang}/${slug}`;

        upsertJsonLd('jsonld-article', {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title[lang],
          description: article.summary[lang],
          image: article.image,
          url,
          inLanguage: lang,
          datePublished: '2026-01-15',
          dateModified: '2026-08-24',
          author: {
            '@type': 'Organization',
            name: 'AllChinaBuy Spreadsheet',
          },
          publisher: {
            '@type': 'Organization',
            name: 'AllChinaBuy Spreadsheet',
            logo: {
              '@type': 'ImageObject',
              url: FAVICON_URL,
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': url,
          },
        });

        upsertJsonLd('jsonld-breadcrumb', {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${SITE_URL}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: article.title[lang],
              item: url,
            },
          ],
        });
      }
    } else {
      upsertJsonLd('jsonld-website', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'AllChinaBuy Spreadsheet',
        url: `${SITE_URL}/`,
        inLanguage: lang,
        description:
          'Explore the AllChinaBuy Spreadsheet hub for product finds, category guides, QC tips, shipping costs and honest reviews.',
      });

      upsertJsonLd('jsonld-organization', {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'AllChinaBuy Spreadsheet',
        url: `${SITE_URL}/`,
        logo: {
          '@type': 'ImageObject',
          url: FAVICON_URL,
        },
      });
    }
  }, [location.pathname]);
}